import { Alert, Field, Input, Spinner } from '@billda/design-system';
import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useValidateGangName } from '../../../hooks/useGangHooks';
import { GangCreationFormData } from '../../../types/gangCreationTypes';
import { StepNavigation } from '../../molecules/StepNavigation';
import { GangLifecycleMode } from '../../../stores/gangLifecycleStore';

const isValidGangNameForValidation = (name: string): boolean => {
  const trimmed = name?.trim() || '';
  return trimmed.length >= 2;
};

export interface GangIdentityStepProps {
  form: UseFormReturn<GangCreationFormData>;
  mode: GangLifecycleMode;
  originalName?: string | null;
  onNext: () => void;
  disableNext?: boolean;
  onSave?: () => void;
  saveDisabled?: boolean;
  showSave?: boolean;
}

export const GangIdentityStep = ({
  form,
  mode,
  originalName = null,
  onNext,
  disableNext = false,
  onSave,
  saveDisabled = false,
  showSave = true,
}: GangIdentityStepProps) => {
  const trimmedName = form.watch('name')?.trim() ?? '';
  const trimmedOriginalName = (originalName ?? '').trim();
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    if (!hasInteracted && trimmedName.length > 0) {
      setHasInteracted(true);
    }
  }, [trimmedName, hasInteracted]);

  const hasEditedName = trimmedName !== trimmedOriginalName;

  const shouldValidate =
    mode === 'create'
      ? isValidGangNameForValidation(trimmedName)
      : hasEditedName && isValidGangNameForValidation(trimmedName);

  const {
    data: validationResult,
    isLoading: isValidating,
    error: validationError,
  } = useValidateGangName(trimmedName, {
    enabled: shouldValidate,
  });

  const fieldState = form.getFieldState('name');
  const isFormValid = !fieldState.error && trimmedName.length >= 2;
  const isServerValid =
    mode === 'edit' && !hasEditedName
      ? true
      : (validationResult?.isValid ?? false);

  const canNavigate =
    !disableNext && isFormValid && (mode === 'edit' ? true : isServerValid);

  const canSave = canNavigate && hasEditedName && isServerValid;

  return (
    <div className="py-4">
      <p className="type-body-md mb-6 text-content-secondary">
        {mode === 'create'
          ? 'Choose a unique name for your gang. This will be used across the platform.'
          : 'Update the gang name. Existing references will reflect the new name immediately.'}
      </p>

      <Controller
        name="name"
        control={form.control}
        render={({ field, fieldState: controllerState }) => {
          const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            if (!hasInteracted) {
              setHasInteracted(true);
            }
            field.onChange(event);
          };

          const hasError =
            !!controllerState.error ||
            (shouldValidate && validationResult && !validationResult.isValid);

          const helperText =
            controllerState.error?.message ||
            (shouldValidate && validationResult && !validationResult.isValid
              ? validationResult.message
              : '') ||
            (validationError ? 'Failed to validate gang name' : '');

          return (
            <div className="mb-4" data-testid="gang-name-input">
              <Field label="Gang Name" error={helperText || undefined}>
                <Input
                  {...field}
                  onChange={handleChange}
                  data-testid="gang-name-input-field"
                  invalid={hasError}
                  endAdornment={
                    isValidating ? <Spinner size={20} /> : undefined
                  }
                />
              </Field>
            </div>
          );
        }}
      />

      {validationResult?.isValid &&
        hasInteracted &&
        (mode === 'create' || hasEditedName) && (
          <Alert
            severity="success"
            className="mb-4"
            data-testid="gang-identity-success-alert"
          >
            Gang name is available!
          </Alert>
        )}

      {mode === 'edit' && !hasEditedName && hasInteracted && (
        <Alert
          severity="info"
          className="mb-4"
          data-testid="gang-identity-info-alert"
        >
          Gang name is unchanged. Update the name to trigger validation if
          needed.
        </Alert>
      )}

      {shouldValidate &&
        !validationResult?.isValid &&
        !isValidating &&
        (validationResult || validationError) && (
          <Alert
            severity="error"
            className="mb-4"
            data-testid="gang-identity-error-alert"
          >
            {validationResult?.message ||
              validationError?.message ||
              'This gang name is not available'}
          </Alert>
        )}

      <StepNavigation
        onNext={onNext}
        nextLabel="Next: Select Leaders"
        disableNext={!canNavigate}
        onSecondary={mode === 'edit' ? onSave : undefined}
        secondaryLabel={mode === 'edit' ? 'Save' : undefined}
        secondaryDisabled={saveDisabled || !canSave || !showSave}
      />
    </div>
  );
};
